/**
 * What a bed's card, tile and row draw for the Seed vault (D69; critique R20, R21; tests 29, 30 and 32), from what the
 * server sends (is_vault, held, its plants): the seed icon, the address card in place of the plants when the vault is
 * empty and nothing waits, and the held seeds' block. And the body a held seed is filed with: the packet review's own
 * item (its rowItem's shape), never a type the app picked.
 *
 * Pure: tested without a screen (seedOrders.d69.test.mjs). Where: LocationCard, LocationTile, LocationRow, HeldSeeds.
 *
 * @module vaultView
 */

import type { PacketItem } from '@/shared/api/packets';

/** What the bed is as the server sends it, in the parts read here. */
export interface VaultBed {
  is_vault?: boolean;
  held?: number;
  plants: ReadonlyArray<{ status: number }>;
}

/** What to draw. */
export interface VaultView {
  /** The seed icon before the name. */
  icon: boolean;
  /** The address card where the plants would be: a vault with no plant growing and nothing waiting. */
  addressCard: boolean;
  /** The held seeds' block above the plants. */
  held: boolean;
}

/**
 * What a bed draws for the vault.
 *
 * @param bed - The bed.
 * @returns VaultView; all false for any other bed.
 */
export function vaultView(bed: VaultBed): VaultView {
  if (!bed.is_vault) return { icon: false, addressCard: false, held: false };
  const waiting = (bed.held ?? 0) > 0;
  const growing = bed.plants.some((plant) => Number(plant.status) === 1);
  return { icon: true, addressCard: !waiting && !growing, held: waiting };
}

/** A held seed as the gardener settled it (Yes to its suggestion, or the change dialog's choice). */
export interface HeldChoice {
  plantId: number | null;
  varietyId?: number | null;
  newVariety?: string | null;
  joinKind?: string | null;
  typeUnsure?: boolean;
  cropId?: number | null;
}

/**
 * The body POST /seed-orders/held/:id/file is sent: Type "Not sure" as the crop and the flag; else its plant and a
 * named variety, or a new one with the kind the server joins to it under a broad flower parent, or the plant alone.
 *
 * @param choice - The held seed as settled.
 * @returns The item (no box, no bed: it goes to the vault).
 */
export function heldItemOf(choice: HeldChoice): Omit<PacketItem, 'box' | 'locationId' | 'newBedName'> {
  if (choice.typeUnsure && choice.cropId != null) return { cropId: choice.cropId, typeUnsure: true };
  const plantId = Number(choice.plantId);
  if (choice.varietyId != null) return { plantId, varietyId: choice.varietyId };
  if (choice.newVariety) return { plantId, newVariety: choice.newVariety, kind: choice.joinKind ?? null };
  return { plantId, varietyId: null };
}

/**
 * Whether a kept seed's "Plan to grow it" must wait. Until the gardener changes the choice, the dialog shows the plant the
 * seed was kept under, and that needs the plant list: a seed kept as Type "Not sure" shows as its crop only once the list is
 * in. So while the list loads (or if the seed came back as its exact plant when it was kept as "Not sure"), it waits. It also
 * waits while a request runs or no plant is chosen.
 *
 * @param input.busy - A request is running.
 * @param input.picked - The gardener has changed the choice.
 * @param input.seed - The kept seed's plant and whether its type is "Not sure".
 * @param input.choice - The dialog's choice now.
 * @param input.loading - The plant list is still loading.
 * @returns True when the action is off.
 */
export function planBlocked(input: {
  busy: boolean;
  picked: boolean;
  seed: { plantId: number | null; typeUnsure?: boolean };
  choice: { plantId: number | null; typeUnsure?: boolean };
  loading: boolean;
}): boolean {
  const { busy, picked, seed, choice, loading } = input;
  if (busy) return true;
  if (!picked && seed.plantId != null && (loading || (Boolean(seed.typeUnsure) && !choice.typeUnsure))) return true;
  return choice.plantId === null && !choice.typeUnsure;
}
